'use client'

import { useEffect, useState, useCallback } from 'react'

const COACH_ITEMS = [
  { label: 'Roster & profiles', desc: 'Players, guardians, waivers', href: '/for-coaches#roster' },
  { label: 'Scheduling', desc: 'Schedule + RSVP + chat', href: '/for-coaches#schedule' },
  { label: 'Payments', desc: 'Collect fees effortlessly', href: '/for-coaches#payments' },
  { label: 'Chat', desc: 'Team chat + 1:1 parent messaging', href: '/for-coaches#chat' },
  { label: 'Game Day', desc: 'Simple scoreboard for every sport', href: '/for-coaches#game-day' },
]

const CLUB_ITEMS = [
  { label: 'Club dashboard', desc: 'Manage all teams from one place', href: '/org/signup' },
  { label: 'Embed widgets', desc: 'Live data on your website', href: '/org/signup#embed' },
  { label: 'REST API', desc: 'Build on Chalk infrastructure', href: '/developers' },
  { label: 'White label', desc: 'Your brand, your domain', href: '/org/signup#white-label' },
  { label: 'Payments platform', desc: 'Revenue share on all transactions', href: '/org/signup#payments' },
]

export function LandingNav() {
  const [coachMenuOpen, setCoachMenuOpen] = useState(false)
  const [clubMenuOpen, setClubMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMobile() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [mobileOpen, closeMobile])

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled || mobileOpen
          ? 'bg-chalkboard/95 backdrop-blur-md border-b border-white/8'
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-8 h-16 flex items-center justify-between">

          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[22px] font-bold text-white leading-none" style={{ fontFamily: 'var(--font-caveat)' }}>
              chalk<span style={{ color: '#F5E642' }}>.</span>
            </span>
          </a>

          {/* Center — desktop dropdowns */}
          <div className="hidden md:flex items-center gap-1">

            {/* For coaches dropdown */}
            <div className="relative">
              <button
                onMouseEnter={() => { setCoachMenuOpen(true); setClubMenuOpen(false) }}
                onMouseLeave={() => setCoachMenuOpen(false)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all duration-150 ${
                  coachMenuOpen
                    ? 'bg-white/8 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                For coaches
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-150 ${coachMenuOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 12 12" fill="none"
                  aria-hidden="true"
                >
                  <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {coachMenuOpen && (
                <div
                  onMouseEnter={() => setCoachMenuOpen(true)}
                  onMouseLeave={() => setCoachMenuOpen(false)}
                  className="absolute top-full left-0 mt-1 w-[240px] bg-chalkboard border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
                >
                  <div className="p-2">
                    {COACH_ITEMS.map(item => (
                      <a
                        key={item.label}
                        href={item.href}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors group"
                      >
                        <div>
                          <p className="text-sm font-semibold text-white group-hover:text-chalk-yellow-hover transition-colors">
                            {item.label}
                          </p>
                          <p className="text-xs text-white/35">{item.desc}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-white/6 p-3">
                    <a
                      href="/signup"
                      className="flex items-center justify-center gap-2 w-full bg-chalk-yellow-hover text-chalk-dark font-bold text-sm rounded-xl py-2.5 hover:bg-chalk-yellow transition-colors"
                    >
                      Start free as a coach →
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* For clubs dropdown */}
            <div className="relative">
              <button
                onMouseEnter={() => { setClubMenuOpen(true); setCoachMenuOpen(false) }}
                onMouseLeave={() => setClubMenuOpen(false)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all duration-150 ${
                  clubMenuOpen
                    ? 'bg-white/8 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                For clubs
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-150 ${clubMenuOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 12 12" fill="none"
                  aria-hidden="true"
                >
                  <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {clubMenuOpen && (
                <div
                  onMouseEnter={() => setClubMenuOpen(true)}
                  onMouseLeave={() => setClubMenuOpen(false)}
                  className="absolute top-full left-0 mt-1 w-[260px] bg-chalkboard border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
                >
                  <div className="p-2">
                    {CLUB_ITEMS.map(item => (
                      <a
                        key={item.label}
                        href={item.href}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors group"
                      >
                        <div>
                          <p className="text-sm font-semibold text-white group-hover:text-chalk-yellow-hover transition-colors">
                            {item.label}
                          </p>
                          <p className="text-xs text-white/35">{item.desc}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-white/6 p-3 space-y-2">
                    <a
                      href="/org/signup"
                      className="flex items-center justify-center gap-2 w-full bg-white/8 border border-white/10 text-white font-semibold text-sm rounded-xl py-2.5 hover:bg-white/12 transition-colors"
                    >
                      Set up your organization →
                    </a>
                    <a
                      href="/developers"
                      className="flex items-center justify-center gap-2 w-full text-[#A19A8E] hover:text-white/60 text-xs transition-colors py-1"
                    >
                      View API documentation
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Sprint 61.57 — trainer top-nav entry. Direct link, no
                dropdown (matches MarketingNav's pattern). Market-analyst
                flagged this as a deliberate bet: no peer competitor
                surfaces a "For trainers" top-nav slot, so a 90-day
                tripwire is documented in the trainer-arc benchmark —
                demote to footer if traffic < 10% of /for-coaches. */}
            <a
              href="/for-trainers"
              className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
            >
              For trainers
            </a>

            <a
              href="/pricing"
              className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
            >
              Pricing
            </a>

          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="/login"
              className="text-sm text-white/50 hover:text-white transition-colors px-3 py-2 hidden sm:block"
            >
              Sign in
            </a>
            <a
              href="/signup"
              className="text-sm font-bold bg-chalk-yellow-hover text-chalk-dark rounded-xl px-4 md:px-5 py-2.5 hover:bg-chalk-yellow transition-colors"
            >
              Get started free
            </a>

            {/* Hamburger — mobile only */}
            <button
              className="md:hidden flex flex-col gap-[5px] p-2 -mr-1 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => setMobileOpen(prev => !prev)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${mobileOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
              <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${mobileOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile nav panel */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-chalkboard pt-16 overflow-y-auto md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="px-6 py-8 space-y-8">

            {/* For coaches section */}
            <div>
              <p className="text-[11px] font-black uppercase tracking-widest mb-4" style={{ color: '#A19A8E' }}>For coaches</p>
              <div className="space-y-1">
                {COACH_ITEMS.map(item => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={closeMobile}
                    className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                  >
                    <div>
                      <p className="text-base font-semibold text-white group-hover:text-chalk-yellow-hover transition-colors">
                        {item.label}
                      </p>
                      <p className="text-sm mt-0.5" style={{ color: '#A19A8E' }}>{item.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* For clubs section */}
            <div>
              <p className="text-[11px] font-black uppercase tracking-widest mb-4" style={{ color: '#A19A8E' }}>For clubs</p>
              <div className="space-y-1">
                {CLUB_ITEMS.map(item => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={closeMobile}
                    className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                  >
                    <div>
                      <p className="text-base font-semibold text-white group-hover:text-chalk-yellow-hover transition-colors">
                        {item.label}
                      </p>
                      <p className="text-sm mt-0.5" style={{ color: '#A19A8E' }}>{item.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Sprint 61.57 — For trainers mobile entry */}
            <div className="border-t border-white/8 pt-6">
              <p className="text-[11px] font-black uppercase tracking-widest mb-4" style={{ color: '#A19A8E' }}>For trainers</p>
              <a
                href="/for-trainers"
                onClick={closeMobile}
                className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <div>
                  <p className="text-base font-semibold text-white group-hover:text-chalk-yellow-hover transition-colors">
                    Run your training business
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: '#A19A8E' }}>
                    Booking, payouts, recurring auto-bill, embed widget — built for solo youth-sports trainers
                  </p>
                </div>
              </a>
            </div>

            {/* Pricing link */}
            <div className="border-t border-white/8 pt-6">
              <a
                href="/pricing"
                onClick={closeMobile}
                className="block px-3 py-3 text-base font-semibold text-white/60 hover:text-white transition-colors"
              >
                Pricing
              </a>
            </div>

            {/* Bottom CTAs */}
            <div className="space-y-3 pt-2">
              <a
                href="/signup"
                onClick={closeMobile}
                className="flex items-center justify-center w-full bg-chalk-yellow-hover text-chalk-dark font-bold text-base rounded-2xl py-4 hover:bg-chalk-yellow transition-colors"
              >
                Get started free
              </a>
              <a
                href="/login"
                onClick={closeMobile}
                className="flex items-center justify-center w-full bg-white/6 border border-white/10 text-white font-semibold text-base rounded-2xl py-4 hover:bg-white/10 transition-colors"
              >
                Sign in
              </a>
            </div>

          </div>
        </div>
      )}
    </>
  )
}
